/**
 * X-Rival province and territory selector.
 * The mapping below is the single source for labels and destination URLs.
 */
(function() {
  'use strict';

  const MAIN_STORE_URL = 'https://xrivalfitness.com/';
  const PROVINCES = Object.freeze({
    'alberta': { name: 'Alberta', url: 'https://edmonton.xrivalfitness.com/', regional: true },
    'british-columbia': { name: 'British Columbia', url: 'https://vancouver.xrivalfitness.com/', regional: true },
    'manitoba': { name: 'Manitoba', url: 'https://xrival.ca/', regional: true },
    'new-brunswick': { name: 'New Brunswick', url: MAIN_STORE_URL },
    'newfoundland-and-labrador': { name: 'Newfoundland and Labrador', url: MAIN_STORE_URL },
    'northwest-territories': { name: 'Northwest Territories', url: MAIN_STORE_URL },
    'nova-scotia': { name: 'Nova Scotia', url: MAIN_STORE_URL },
    'nunavut': { name: 'Nunavut', url: MAIN_STORE_URL },
    'ontario': { name: 'Ontario', url: MAIN_STORE_URL },
    'prince-edward-island': { name: 'Prince Edward Island', url: MAIN_STORE_URL },
    'quebec': { name: 'Quebec', url: MAIN_STORE_URL },
    'saskatchewan': { name: 'Saskatchewan', url: MAIN_STORE_URL },
    'yukon': { name: 'Yukon', url: MAIN_STORE_URL }
  });

  const STORAGE_KEY = 'xrival_province';
  const TIMESTAMP_KEY = 'xrival_province_ts';
  const LEGACY_KEY = 'xrival_store';
  const LEGACY_TIMESTAMP_KEY = 'xrival_store_ts';
  // Saved preferences only supply labels; they never suppress a new entry prompt.
  const PREFERENCE_TTL_MS = 60 * 60 * 1000;
  const VISIT_KEY = 'xrival_main_visit_v2';
  const INTERNAL_NAV_KEY = 'xrival_main_internal_navigation_v2';
  const LEGACY_ALIASES = Object.freeze({ edmonton: 'alberta', vancouver: 'british-columbia', winnipeg: 'manitoba' });

  function cookieDomain() {
    const hostname = window.location.hostname;
    return hostname === 'xrivalfitness.com' || hostname.endsWith('.xrivalfitness.com') ? '.xrivalfitness.com' : null;
  }
  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 86400000);
    const domain = cookieDomain();
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires.toUTCString() + '; path=/; SameSite=Lax' + (domain ? '; domain=' + domain : '');
  }
  function getCookie(name) {
    const prefix = name + '=';
    const item = document.cookie.split(';').map(function(part) { return part.trim(); }).find(function(part) { return part.indexOf(prefix) === 0; });
    if (!item) return null;
    try { return decodeURIComponent(item.slice(prefix.length)); } catch (error) { return item.slice(prefix.length); }
  }
  function readStorage(name) { try { return window.localStorage.getItem(name); } catch (error) { return null; } }
  function writeStorage(name, value) { try { window.localStorage.setItem(name, value); } catch (error) {} }
  function normalize(value) {
    if (!value) return null;
    if (Object.prototype.hasOwnProperty.call(PROVINCES, value)) return value;
    return Object.prototype.hasOwnProperty.call(LEGACY_ALIASES, value) ? LEGACY_ALIASES[value] : null;
  }
  function isMainHost(hostname) {
    return ['xrivalfitness.com', 'www.xrivalfitness.com', 'ccc94a-2.myshopify.com'].indexOf(hostname) !== -1;
  }
  function isMainDomain() { return isMainHost(window.location.hostname); }
  function readSession(key) {
    try { return JSON.parse(window.sessionStorage.getItem(key)); } catch (error) { return null; }
  }
  function writeSession(key, value) {
    try { window.sessionStorage.setItem(key, JSON.stringify(value)); } catch (error) {}
  }
  function readVisit() {
    return readSession(VISIT_KEY) || (window.history.state && window.history.state[VISIT_KEY]) || null;
  }
  function writeVisit(visit) {
    writeSession(VISIT_KEY, visit);
    // Keep reloads working when a browser blocks sessionStorage.
    try {
      const state = Object.assign({}, window.history.state || {});
      state[VISIT_KEY] = visit;
      window.history.replaceState(state, '', window.location.href);
    } catch (error) {}
  }
  function navigationType() {
    const entries = window.performance && window.performance.getEntriesByType('navigation');
    if (entries && entries[0]) return entries[0].type;
    const legacy = window.performance && window.performance.navigation;
    return legacy && legacy.type === 1 ? 'reload' : legacy && legacy.type === 2 ? 'back_forward' : 'navigate';
  }
  function beginVisit(handoffProvince) {
    if (!isMainDomain()) return;
    const previous = readVisit();
    const type = navigationType();
    const internal = readSession(INTERNAL_NAV_KEY);
    try { window.sessionStorage.removeItem(INTERNAL_NAV_KEY); } catch (error) {}
    let internalReferrer = false;
    if (document.referrer) {
      try { internalReferrer = isMainHost(new URL(document.referrer).hostname); } catch (error) {}
    }
    const internalNavigation = internal && Date.now() - internal.at < 30000 &&
      internal.url === window.location.origin + window.location.pathname + window.location.search;
    if (handoffProvince && !PROVINCES[handoffProvince].regional) {
      writeVisit({ confirmed: true, province: handoffProvince, departedToRegional: false });
    } else if (type === 'reload' || type === 'back_forward') {
      writeVisit(previous && !previous.departedToRegional ? previous : { confirmed: false });
    } else if (internalReferrer || (!document.referrer && internalNavigation)) {
      const province = previous && previous.confirmed && !previous.departedToRegional
        ? normalize(previous.province) : getSelectedProvince();
      writeVisit({ confirmed: !!province && !PROVINCES[province].regional, province: province, departedToRegional: false });
    } else {
      // External links and direct/bookmarked entries always get a fresh choice.
      writeVisit({ confirmed: false });
    }
  }
  function rememberInternalNavigation(url) {
    if (!isMainDomain() || !isMainHost(url.hostname)) return;
    writeSession(INTERNAL_NAV_KEY, { url: url.origin + url.pathname + url.search, at: Date.now() });
  }
  function trackInternalNavigation() {
    document.addEventListener('click', function(event) {
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest && event.target.closest('a[href]');
      if (!anchor || anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
      rememberInternalNavigation(url);
    });
    document.addEventListener('submit', function(event) {
      const form = event.target;
      if (event.defaultPrevented || (form.target && form.target !== '_self')) return;
      const url = new URL(form.action || window.location.href, window.location.href);
      if ((form.method || 'get').toLowerCase() === 'get') {
        url.search = new URLSearchParams(new FormData(form)).toString();
      }
      rememberInternalNavigation(url);
    });
  }
  function inferFromDomain() {
    const hostname = window.location.hostname;
    if (hostname === 'edmonton.xrivalfitness.com') return 'alberta';
    if (hostname === 'vancouver.xrivalfitness.com') return 'british-columbia';
    if (hostname === 'winnipeg.xrivalfitness.com' || hostname === 'xrival.ca' || hostname === 'www.xrival.ca') return 'manitoba';
    return null;
  }
  function saveProvince(province) {
    if (!normalize(province)) return;
    const timestamp = String(Date.now());
    writeStorage(STORAGE_KEY, province);
    writeStorage(TIMESTAMP_KEY, timestamp);
    setCookie(STORAGE_KEY, province, PREFERENCE_TTL_MS / 86400000);
    setCookie(TIMESTAMP_KEY, timestamp, PREFERENCE_TTL_MS / 86400000);
  }
  function readSelection(read) {
    const province = normalize(read(STORAGE_KEY));
    const legacy = normalize(read(LEGACY_KEY));
    const timestamp = province
      ? read(TIMESTAMP_KEY) || (legacy === province ? read(LEGACY_TIMESTAMP_KEY) : null)
      : read(LEGACY_TIMESTAMP_KEY);
    const selected = province || legacy;
    const selectedAt = Number(timestamp);
    return selected && Number.isFinite(selectedAt) && selectedAt > 0
      ? { province: selected, timestamp: selectedAt }
      : null;
  }
  function getSelectedProvince() {
    const records = [readSelection(readStorage), readSelection(getCookie)].filter(Boolean);
    records.sort(function(a, b) { return b.timestamp - a.timestamp; });
    const latest = records[0];
    const elapsed = latest ? Date.now() - latest.timestamp : -1;
    return latest && elapsed >= 0 && elapsed < PREFERENCE_TTL_MS ? latest.province : null;
  }
  function consumeHandoff() {
    const handoffProvince = normalize(new URLSearchParams(window.location.search).get('xrival_province'));
    if (handoffProvince && isMainDomain()) {
      saveProvince(handoffProvince);
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('xrival_province');
      window.history.replaceState(window.history.state, '', cleanUrl.toString());
      return handoffProvince;
    }
    return null;
  }
  function buildPath(target) { return (target.charAt(target.length - 1) === '/' ? target.slice(0, -1) : target) + (window.location.pathname || '/') + window.location.search + window.location.hash; }
  function redirectToProvince(province) {
    const config = PROVINCES[province];
    if (!config) return;
    saveProvince(province);
    if (isMainDomain()) {
      writeVisit({ confirmed: !config.regional, province: province, departedToRegional: !!config.regional });
    }
    const headerCurrent = document.getElementById('xrival-store-selector-current');
    if (headerCurrent) {
      headerCurrent.textContent = config.name;
      headerCurrent.dataset.province = province;
    }
    document.querySelectorAll('.xrival-store-selector__option').forEach(function(option) {
      option.classList.toggle('is-selected', option.dataset.province === province);
    });
    const popup = document.querySelector('.xrival-location-popup-overlay');
    if (popup) popup.remove();
    document.documentElement.classList.remove('xrival-lock');
    if (!config.regional) {
      // A main-store region keeps the current main-store page and its URL intact.
      if (!isMainDomain()) {
        const handoff = new URL(buildPath(MAIN_STORE_URL));
        handoff.searchParams.set('xrival_province', province);
        window.location.assign(handoff.toString());
      }
      return;
    }
    const destination = buildPath(config.url);
    if (destination !== window.location.href) window.location.assign(destination);
  }
  function createOption(province, className) {
    const option = document.createElement('button');
    option.type = 'button';
    option.className = className;
    option.dataset.province = province;
    option.textContent = PROVINCES[province].name;
    return option;
  }
  function initStoreSelector() {
    const button = document.getElementById('xrival-store-selector-btn');
    const dropdown = document.getElementById('xrival-store-selector-dropdown');
    const current = document.getElementById('xrival-store-selector-current');
    if (!button || !dropdown || !current) return;
    dropdown.replaceChildren();
    Object.keys(PROVINCES).forEach(function(province) {
      const option = createOption(province, 'xrival-store-selector__option');
      option.setAttribute('role', 'menuitem');
      dropdown.appendChild(option);
    });
    const visit = readVisit();
    const selected = inferFromDomain() || (visit && visit.confirmed ? normalize(visit.province) : null);
    current.textContent = selected ? PROVINCES[selected].name : 'Select Province';
    if (selected) current.dataset.province = selected;
    const options = Array.from(dropdown.querySelectorAll('.xrival-store-selector__option'));
    options.forEach(function(option) { option.classList.toggle('is-selected', option.dataset.province === selected); });
    let isOpen = false;
    function toggle(open) {
      isOpen = open !== undefined ? open : !isOpen;
      button.setAttribute('aria-expanded', String(isOpen));
      dropdown.classList.toggle('is-open', isOpen);
      if (isOpen && window.innerWidth >= 990) dropdown.addEventListener('mouseleave', leave);
      else dropdown.removeEventListener('mouseleave', leave);
    }
    function leave() { if (window.innerWidth >= 990) toggle(false); }
    button.addEventListener('click', function(event) { event.stopPropagation(); toggle(); });
    if (window.innerWidth >= 990) button.addEventListener('mouseenter', function() { toggle(true); });
    options.forEach(function(option) { option.addEventListener('click', function(event) { event.stopPropagation(); toggle(false); redirectToProvince(option.dataset.province); }); });
    document.addEventListener('click', function(event) { if (isOpen && !button.contains(event.target) && !dropdown.contains(event.target)) toggle(false); });
    document.addEventListener('keydown', function(event) { if (event.key === 'Escape' && isOpen) { toggle(false); button.focus(); } });
  }
  function createPopup() {
    const visit = readVisit();
    if (!isMainDomain() || (visit && visit.confirmed && !visit.departedToRegional) || document.querySelector('.xrival-location-popup-overlay')) return;
    const overlay = document.createElement('div');
    overlay.className = 'xrival-location-popup-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'xrival-popup-title');
    const modal = document.createElement('div');
    modal.className = 'xrival-location-popup-modal';
    modal.innerHTML = '<div class="xrival-location-popup-content"><p class="xrival-location-popup-eyebrow">SHOP ONLINE ACROSS CANADA</p><h2 id="xrival-popup-title" class="xrival-location-popup-title">Select Your Province or Territory</h2><p class="xrival-location-popup-subtitle">Choose your region to continue to X-Rival’s online fitness equipment store.</p><div class="xrival-location-popup-options" aria-label="Canadian provinces and territories"></div></div>';
    const options = modal.querySelector('.xrival-location-popup-options');
    Object.keys(PROVINCES).forEach(function(province) {
      const option = createOption(province, 'xrival-location-popup-option');
      options.appendChild(option);
      option.addEventListener('click', function() { document.documentElement.classList.remove('xrival-lock'); redirectToProvince(province); });
    });
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    document.documentElement.classList.add('xrival-lock');
    overlay.addEventListener('click', function(event) { if (event.target === overlay) event.stopPropagation(); });
  }
  function init() {
    beginVisit(consumeHandoff());
    function ready() { initStoreSelector(); trackInternalNavigation(); createPopup(); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
    else ready();
  }
  window.addEventListener('pageshow', function(event) {
    if (event.persisted && isMainDomain() && document.body) {
      const visit = readVisit();
      if (visit && visit.departedToRegional) writeVisit({ confirmed: false });
      const current = document.getElementById('xrival-store-selector-current');
      const selected = visit && visit.confirmed && !visit.departedToRegional ? normalize(visit.province) : null;
      if (current) {
        current.textContent = selected ? PROVINCES[selected].name : 'Select Province';
        if (selected) current.dataset.province = selected;
        else delete current.dataset.province;
      }
      document.querySelectorAll('.xrival-store-selector__option').forEach(function(option) {
        option.classList.toggle('is-selected', option.dataset.province === selected);
      });
      createPopup();
    }
  });
  init();
})();
